<template>
  <div class="td-page">
    <router-link class="td-back" :to="{ name: 'admin-termin' }">
      ← Alle Beratungswünsche
    </router-link>

    <p v-if="error" class="td-error">⚠️ {{ error }}</p>
    <p v-else-if="!b" class="td-loading">Lade Beratungswunsch…</p>

    <template v-if="b">
      <!-- ============ KOPF ============ -->
      <header class="td-head">
        <div class="td-head__main">
          <div class="td-head__name-row">
            <h1>{{ b.name || "Ohne Namen" }}</h1>
            <span class="td-status" :class="'td-status--' + b.status">{{ statusLabel(b.status) }}</span>
            <span v-if="b.source" class="td-source">{{ b.source }}</span>
          </div>
          <p class="td-head__meta">
            Beratungswunsch #{{ b.booking_id }} · eingegangen {{ fmtDateTime(b.created_date) }}
          </p>
        </div>
        <div class="td-when">
          <span class="td-when__label">Termin</span>
          <span class="td-when__date">{{ fmtDateLong(b.date) }}</span>
          <span class="td-when__time">{{ b.time }} Uhr · {{ b.duration }} Min.</span>
        </div>
      </header>

      <!-- ============ AKTIONEN ============ -->
      <div class="td-actions">
        <button v-if="b.phone" class="btn btn--call" @click="openPhone">📞 anrufen</button>
        <a class="btn" :href="'mailto:' + b.email" @click.prevent="scrollToMail">✉️ Mail schreiben</a>
        <button class="btn" :disabled="b.status === 1" @click="setStatus('booking_confirm')">✓ bestätigen</button>
        <button class="btn btn--danger" :disabled="b.status === 2" @click="cancelBooking">absagen</button>
        <button class="btn btn--danger" @click="deleteBooking">löschen</button>
      </div>

      <div class="td-grid">
        <!-- ============ LINKS: KONTAKT + NOTIZ ============ -->
        <div class="td-col">
          <section class="td-card">
            <h2 class="td-card__h">Kontaktdaten</h2>
            <label class="td-field">
              <span>Name</span>
              <input v-model="form.booking_name" type="text" />
            </label>
            <div class="td-field-row">
              <label class="td-field">
                <span>E-Mail</span>
                <input v-model="form.booking_email" type="email" />
              </label>
              <label class="td-field">
                <span>Telefon</span>
                <input v-model="form.booking_phone" type="tel" />
              </label>
            </div>
            <div class="td-field-row">
              <label class="td-field">
                <span>Geburtsdatum (TT.MM.JJJJ)</span>
                <input v-model="form.booking_birthdate" type="text" placeholder="z.B. 01.03.1961" />
              </label>
              <label class="td-field">
                <span>Geschlecht</span>
                <select v-model="form.booking_gender">
                  <option value="">—</option>
                  <option value="weiblich">weiblich</option>
                  <option value="männlich">männlich</option>
                  <option value="divers">divers</option>
                </select>
              </label>
            </div>
            <label class="td-field">
              <span>Versicherungsstatus</span>
              <select v-model="form.booking_insurance">
                <option value="">—</option>
                <option value="gesetzliche_krankenkasse">Gesetzliche Krankenkasse</option>
                <option value="heilfuersorge">Heilfürsorge</option>
                <option value="gesetzliche_krankenkasse_beihilfe">Gesetzliche Krankenkasse und Beihilfe</option>
                <option value="freie_heilfuersorge_beihilfe">Freie Heilfürsorge &amp; Beihilfe</option>
              </select>
            </label>
            <label class="td-field">
              <span>Nachricht des Kunden</span>
              <textarea v-model="form.booking_message" rows="4"></textarea>
            </label>
            <button class="btn btn--primary" :disabled="!contactDirty || saving" @click="saveContact">
              {{ saving ? "Speichern…" : "Änderungen speichern" }}
            </button>
          </section>

          <section class="td-card">
            <h2 class="td-card__h">Interne Notiz <small>(sieht nur das Team)</small></h2>
            <textarea v-model="form.booking_note" rows="5" class="td-note"
                      placeholder="Gesprächsergebnis, Besonderheiten, nächste Schritte…"></textarea>
            <button class="btn btn--primary" :disabled="!noteDirty || saving" @click="saveNote">
              Notiz speichern
            </button>
          </section>

          <section class="td-card">
            <h2 class="td-card__h">Anrufhistorie</h2>
            <p v-if="!calls.length" class="td-muted">Noch keine Anrufe zu diesem Termin.</p>
            <ul v-else class="td-calls">
              <li v-for="c in calls" :key="c.call_id">
                <span class="td-calls__icon" :class="'td-calls__icon--' + c.status">📞</span>
                <span class="td-calls__what">
                  {{ c.to_number }}
                  <small>{{ callStatusLabel(c.status) }}<template v-if="c.duration_sec"> · {{ fmtDur(c.duration_sec) }}</template></small>
                </span>
                <span class="td-calls__when">{{ fmtDateTime(c.created_date) }}</span>
              </li>
            </ul>
          </section>
        </div>

        <!-- ============ RECHTS: UMBUCHEN + MAIL ============ -->
        <div class="td-col">
          <section class="td-card td-card--accent">
            <h2 class="td-card__h">Termin verschieben</h2>
            <p class="td-muted">
              Aktuell: <strong>{{ fmtDateLong(b.date) }}, {{ b.time }} Uhr</strong>
            </p>
            <label class="td-field">
              <span>Neuer Termin</span>
              <select v-model="reschedule.slotId">
                <option :value="0" disabled>— freien Termin wählen —</option>
                <option v-for="s in freeSlots" :key="s.slot_id" :value="s.slot_id">
                  {{ fmtDateLong(s.date) }} · {{ s.time }} Uhr
                </option>
              </select>
            </label>
            <label class="td-check">
              <input v-model="reschedule.sendMail" type="checkbox" />
              <span>Kunde per Mail über den neuen Termin informieren</span>
            </label>
            <button class="btn btn--primary" :disabled="!reschedule.slotId || saving" @click="doReschedule">
              Termin umbuchen
            </button>
          </section>

          <section class="td-card" ref="mailCard">
            <h2 class="td-card__h">E-Mail an {{ b.name || "den Kunden" }}</h2>
            <p class="td-muted">an <strong>{{ b.email }}</strong> · Absender sprechstunde@waizmann089.de</p>
            <label class="td-field">
              <span>Betreff</span>
              <input v-model="mail.subject" type="text" />
            </label>
            <label class="td-field">
              <span>Nachricht</span>
              <textarea v-model="mail.body" rows="9"></textarea>
            </label>
            <button class="btn btn--primary" :disabled="!mail.subject || !mail.body || mail.sending" @click="sendMail">
              {{ mail.sending ? "Wird gesendet…" : "✉️ Mail senden" }}
            </button>
            <p v-if="mail.sentInfo" class="td-mail-ok">{{ mail.sentInfo }}</p>
          </section>
        </div>
      </div>
    </template>

    <div v-if="flash" class="admin-flash">{{ flash }}</div>

    <PhoneDialog
      v-if="phoneDialog.open"
      :phone="phoneDialog.phone"
      :booking-id="phoneDialog.bookingId"
      :contact-name="phoneDialog.name"
      @close="phoneDialog.open = false; loadCalls();"
    />
  </div>
</template>

<script>
import BaseCalls from "@/services/BaseCalls.js";
import PhoneDialog from "@/components/sites/admin/PhoneDialog.vue";

export default {
  name: "AdminTerminDetail",
  components: { PhoneDialog },
  data() {
    return {
      b: null,
      error: "",
      calls: [],
      freeSlots: [],
      form: {
        booking_name: "", booking_email: "", booking_phone: "",
        booking_birthdate: "", booking_gender: "", booking_insurance: "",
        booking_message: "", booking_note: "",
      },
      reschedule: { slotId: 0, sendMail: true },
      mail: { subject: "", body: "", sending: false, sentInfo: "" },
      saving: false,
      flash: "",
      phoneDialog: { open: false, phone: "", bookingId: 0, name: "" },
    };
  },
  computed: {
    bookingId() {
      return parseInt(this.$route.params.id, 10) || 0;
    },
    contactDirty() {
      if (!this.b) return false;
      return this.form.booking_name !== (this.b.name || "")
        || this.form.booking_email !== (this.b.email || "")
        || this.form.booking_phone !== (this.b.phone || "")
        || this.form.booking_birthdate !== (this.b.birthdate || "")
        || this.form.booking_gender !== (this.b.gender || "")
        || this.form.booking_insurance !== (this.b.insurance || "")
        || this.form.booking_message !== (this.b.message || "");
    },
    noteDirty() {
      return this.b && this.form.booking_note !== (this.b.note || "");
    },
  },
  mounted() {
    this.load();
    this.loadCalls();
    this.loadFreeSlots();
  },
  methods: {
    statusLabel(s) {
      return ({ 0: "neu", 1: "bestätigt", 2: "abgesagt" })[s] || s;
    },
    callStatusLabel(s) {
      return ({
        completed: "geführt", busy: "besetzt", "no-answer": "nicht erreicht",
        canceled: "abgebrochen", failed: "fehlgeschlagen",
        dialing: "wählt…", ringing: "klingelt…", "in-progress": "läuft…",
      })[s] || s;
    },
    fmtDateLong(iso) {
      if (!iso) return "";
      const d = new Date(iso + "T00:00:00");
      const wd = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][d.getDay()];
      return `${wd}, ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
    },
    fmtDateTime(v) {
      if (!v) return "";
      const d = new Date(String(v).replace(" ", "T"));
      if (isNaN(d)) return String(v);
      return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
        + ", " + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) + " Uhr";
    },
    fmtDur(sec) {
      const m = Math.floor(sec / 60), s = sec % 60;
      return m ? `${m}:${String(s).padStart(2, "0")} Min.` : `${s} Sek.`;
    },
    showFlash(msg) {
      this.flash = msg;
      setTimeout(() => (this.flash = ""), 2600);
    },

    async load() {
      const r = await BaseCalls.terminAdminGet({ action: "booking", id: this.bookingId });
      if (!r?.data?.success) {
        this.error = r?.data?.message || "Beratungswunsch nicht gefunden.";
        return;
      }
      this.b = r.data.booking;
      this.form = {
        booking_name:      this.b.name || "",
        booking_email:     this.b.email || "",
        booking_phone:     this.b.phone || "",
        booking_birthdate: this.b.birthdate || "",
        booking_gender:    this.b.gender || "",
        booking_insurance: this.b.insurance || "",
        booking_message:   this.b.message || "",
        booking_note:      this.b.note || "",
      };
      if (!this.mail.subject) {
        this.mail.subject = `Ihr Termin am ${this.fmtDateLong(this.b.date)} um ${this.b.time} Uhr`;
        this.mail.body = `Hallo ${this.b.name},\n\n\n\nViele Grüße\nIhre ZZV-Sprechstunde — Waizmann.089`;
      }
    },
    async loadCalls() {
      try {
        const r = await BaseCalls.phoneCalls(this.bookingId);
        this.calls = r?.data?.calls || [];
      } catch (_e) { /* Telefon evtl. nicht konfiguriert */ }
    },
    async loadFreeSlots() {
      const r = await BaseCalls.terminAdminGet({ action: "free_slots", id: 60 });
      this.freeSlots = r?.data?.slots || [];
    },

    async saveContact() {
      this.saving = true;
      const r = await BaseCalls.terminAdminPost({
        action: "booking_update",
        booking_id: this.bookingId,
        booking_name: this.form.booking_name,
        booking_email: this.form.booking_email,
        booking_phone: this.form.booking_phone,
        booking_birthdate: this.form.booking_birthdate,
        booking_gender: this.form.booking_gender,
        booking_insurance: this.form.booking_insurance,
        booking_message: this.form.booking_message,
      });
      this.saving = false;
      this.showFlash(r?.data?.success ? "Kontaktdaten gespeichert." : (r?.data?.message || "Fehler beim Speichern."));
      if (r?.data?.success) this.load();
    },
    async saveNote() {
      this.saving = true;
      const r = await BaseCalls.terminAdminPost({
        action: "booking_update",
        booking_id: this.bookingId,
        booking_note: this.form.booking_note,
      });
      this.saving = false;
      this.showFlash(r?.data?.success ? "Notiz gespeichert." : "Fehler beim Speichern.");
      if (r?.data?.success) this.load();
    },

    async setStatus(action) {
      const r = await BaseCalls.terminAdminPost({ action, booking_id: this.bookingId });
      this.showFlash(r?.data?.success ? "Status geändert." : "Fehler.");
      this.load();
    },
    async cancelBooking() {
      if (!confirm("Termin absagen und Absage-Mail an den Kunden senden?")) return;
      const r = await BaseCalls.terminAdminPost({
        action: "booking_cancel", booking_id: this.bookingId, send_mail: true,
      });
      this.showFlash(r?.data?.success ? "Abgesagt — Mail ist raus." : "Fehler.");
      this.load();
    },
    async deleteBooking() {
      if (!confirm("Beratungswunsch endgültig löschen?")) return;
      const r = await BaseCalls.terminAdminPost({ action: "booking_delete", booking_id: this.bookingId });
      if (r?.data?.success) this.$router.replace({ name: "admin-termin" });
    },

    async doReschedule() {
      const target = this.freeSlots.find((s) => s.slot_id === this.reschedule.slotId);
      if (!target) return;
      if (!confirm(`Termin auf ${this.fmtDateLong(target.date)}, ${target.time} Uhr umbuchen?`)) return;
      this.saving = true;
      const r = await BaseCalls.terminAdminPost({
        action: "booking_reschedule",
        booking_id: this.bookingId,
        new_slot_id: this.reschedule.slotId,
        send_mail: this.reschedule.sendMail,
      });
      this.saving = false;
      if (r?.data?.success) {
        this.showFlash(this.reschedule.sendMail ? "Umgebucht — Kunde ist informiert." : "Umgebucht.");
        this.reschedule.slotId = 0;
        this.mail.subject = "";
        await this.load();
        this.loadFreeSlots();
      } else {
        this.showFlash(r?.data?.message || "Umbuchung fehlgeschlagen.");
      }
    },

    scrollToMail() {
      this.$refs.mailCard?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    async sendMail() {
      this.mail.sending = true;
      this.mail.sentInfo = "";
      const r = await BaseCalls.terminAdminPost({
        action: "booking_send_mail",
        booking_id: this.bookingId,
        subject: this.mail.subject,
        body: this.mail.body,
      });
      this.mail.sending = false;
      if (r?.data?.success) {
        this.mail.sentInfo = `Gesendet an ${this.b.email} — ${new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr.`;
        this.showFlash("Mail gesendet.");
      } else {
        this.showFlash(r?.data?.message || "Mailversand fehlgeschlagen.");
      }
    },

    openPhone() {
      this.phoneDialog = {
        open: true,
        phone: this.b.phone || "",
        bookingId: this.bookingId,
        name: this.b.name || "",
      };
    },
  },
};
</script>

<style scoped>
.td-page { max-width: 1100px; margin: 0 auto; padding: 24px 16px 60px; }
.td-back {
  display: inline-block; margin-bottom: 18px;
  color: #2a6bf2; font-weight: 700; text-decoration: none; font-size: .95em;
}
.td-back:hover { text-decoration: underline; }
.td-error, .td-loading { padding: 32px; text-align: center; color: #666; }
.td-error { color: #b91c1c; }

/* ---- Kopf ---- */
.td-head {
  display: flex; justify-content: space-between; align-items: stretch;
  gap: 20px; flex-wrap: wrap; margin-bottom: 16px;
}
.td-head__name-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.td-head h1 { margin: 0; font-size: clamp(24px, 3.4vw, 34px); letter-spacing: -0.5px; }
.td-head__meta { margin: 6px 0 0; color: #667; font-size: .92em; }
.td-status {
  border-radius: 999px; padding: 3px 12px;
  font-size: .78em; font-weight: 900; text-transform: uppercase; letter-spacing: .5px;
}
.td-status--0 { background: #fdecea; color: #b91c1c; }
.td-status--1 { background: #e8f7ee; color: #1d7a43; }
.td-status--2 { background: #f1f1f3; color: #777; }
.td-source {
  background: #eef5ff; color: #143a86; border: 1px solid #c9dcf8;
  border-radius: 999px; padding: 2px 10px; font-size: .8em; white-space: nowrap;
}
.td-when {
  background: linear-gradient(135deg, #2a6bf2, #143a86);
  color: #fff; border-radius: 14px; padding: 14px 22px;
  display: flex; flex-direction: column; justify-content: center; gap: 2px;
  min-width: 230px;
}
.td-when__label {
  font-size: 10px; font-weight: 900; letter-spacing: 1.6px;
  text-transform: uppercase; opacity: .75;
}
.td-when__date { font-size: 20px; font-weight: 900; letter-spacing: -0.3px; }
.td-when__time { font-size: 14px; opacity: .9; }

/* ---- Aktionen ---- */
.td-actions {
  display: flex; gap: 8px; flex-wrap: wrap;
  padding: 12px 0 20px; border-bottom: 1px solid #e3e7ef; margin-bottom: 22px;
}

/* ---- Grid + Karten ---- */
.td-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 18px; align-items: start;
}
@media (max-width: 900px) { .td-grid { grid-template-columns: 1fr; } }
.td-col { display: flex; flex-direction: column; gap: 18px; }
.td-card {
  background: #fff; border: 1.5px solid #e3e7ef; border-radius: 14px;
  padding: 20px 18px;
}
.td-card--accent { border-color: #c9dcf8; background: #fbfdff; }
.td-card__h { margin: 0 0 14px; font-size: 17px; letter-spacing: -0.2px; }
.td-card__h small { color: #889; font-weight: 400; font-size: .75em; }
.td-muted { color: #667; font-size: .9em; margin: 0 0 12px; }

.td-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 12px; font-size: 13px; font-weight: 700; color: #333; }
.td-field input, .td-field textarea, .td-field select {
  font: inherit; font-weight: 400; padding: 9px 11px;
  border: 1.5px solid #d4dae4; border-radius: 8px; background: #fff;
}
.td-field input:focus, .td-field textarea:focus, .td-field select:focus {
  outline: none; border-color: #2a6bf2;
}
.td-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media (max-width: 520px) { .td-field-row { grid-template-columns: 1fr; } }
.td-note { width: 100%; font: inherit; padding: 10px 12px; border: 1.5px solid #d4dae4; border-radius: 8px; margin-bottom: 12px; background: #fffdf2; }
.td-check { display: flex; align-items: center; gap: 8px; font-size: .9em; margin: 0 0 12px; cursor: pointer; }

/* ---- Buttons (Basis wie Admin-Liste) ---- */
.btn {
  padding: 8px 14px; border: 1px solid #ccd; background: #fff; border-radius: 8px;
  cursor: pointer; font: inherit; font-size: .92em; text-decoration: none; color: inherit;
  display: inline-flex; align-items: center; gap: 6px;
}
.btn:disabled { opacity: .5; cursor: not-allowed; }
.btn--primary { background: #2a6bf2; color: #fff; border-color: #2a6bf2; font-weight: 700; }
.btn--danger  { color: #c0392b; border-color: #f0c8c4; }
.btn--call    { background: #1d7a43; border-color: #1d7a43; color: #fff; font-weight: 700; }

/* ---- Anrufhistorie ---- */
.td-calls { list-style: none; margin: 0; padding: 0; }
.td-calls li {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 0; border-bottom: 1px solid #f0f2f7; font-size: .92em;
}
.td-calls li:last-child { border-bottom: none; }
.td-calls__icon--completed { filter: none; }
.td-calls__what { flex: 1; font-weight: 600; }
.td-calls__what small { display: block; color: #889; font-weight: 400; }
.td-calls__when { color: #889; font-size: .88em; white-space: nowrap; }

.td-mail-ok { margin: 10px 0 0; color: #1d7a43; font-weight: 700; font-size: .9em; }

.admin-flash {
  position: fixed; bottom: 24px; right: 24px;
  background: #143a86; color: white; padding: 10px 16px; border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0,0,0,.15); z-index: 50;
}
</style>
